import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { securityApi, type SecurityFeatureFlags } from "@/services/security.service";
import { useAuth } from "@/hooks/useAuthV2";

export function SecurityFeatureFlagsPanel() {
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const can = hasPermission("security.manage") || hasPermission("admin.access");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "security", "feature-flags"],
    queryFn: () => securityApi.getFeatureFlags(),
    enabled: can,
  });

  const [draft, setDraft] = React.useState<Partial<SecurityFeatureFlags>>({});

  React.useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => securityApi.patchFeatureFlags(draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "security"] });
      void qc.invalidateQueries({ queryKey: ["security"] });
      toast({ title: "Флаги безопасности сохранены" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка сохранения", description: e.message, variant: "destructive" }),
  });

  if (!can) {
    return <p className="text-sm text-muted-foreground">Недостаточно прав (security.manage).</p>;
  }
  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Загрузка…</p>;
  }

  return (
    <div className="space-y-4 max-w-lg">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="tf">Модуль 2FA</Label>
        <Switch
          id="tf"
          checked={!!draft.two_factor_enabled}
          onCheckedChange={(v) => setDraft((d) => ({ ...d, two_factor_enabled: v }))}
        />
      </div>
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="sl">Модуль блокировки экрана</Label>
        <Switch
          id="sl"
          checked={!!draft.screen_lock_enabled}
          onCheckedChange={(v) => setDraft((d) => ({ ...d, screen_lock_enabled: v }))}
        />
      </div>
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="r2">Обязательная 2FA для всех</Label>
        <Switch
          id="r2"
          checked={!!draft.require_2fa_for_all}
          onCheckedChange={(v) => setDraft((d) => ({ ...d, require_2fa_for_all: v }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>PIN min</Label>
          <Input
            type="number"
            value={draft.pin_min_length ?? 6}
            onChange={(e) =>
              setDraft((d) => ({ ...d, pin_min_length: Number(e.target.value) }))
            }
          />
        </div>
        <div>
          <Label>PIN max</Label>
          <Input
            type="number"
            value={draft.pin_max_length ?? 6}
            onChange={(e) =>
              setDraft((d) => ({ ...d, pin_max_length: Number(e.target.value) }))
            }
          />
        </div>
        <div>
          <Label>Макс. попыток PIN</Label>
          <Input
            type="number"
            value={draft.max_pin_attempts ?? 5}
            onChange={(e) =>
              setDraft((d) => ({ ...d, max_pin_attempts: Number(e.target.value) }))
            }
          />
        </div>
        <div>
          <Label>Idle min (сек)</Label>
          <Input
            type="number"
            value={draft.idle_timeout_min_seconds ?? 60}
            onChange={(e) =>
              setDraft((d) => ({ ...d, idle_timeout_min_seconds: Number(e.target.value) }))
            }
          />
        </div>
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        Сохранить
      </Button>
    </div>
  );
}
